import {
  JourneyContext,
  JourneyState,
  VehiclePageCustomisation,
  VehicleType
} from '../common/CommonTypes';
/**
 * Test fixtures and helpers for journey-related unit tests.
 *
 * Includes pre-canned `VehicleType` objects (`testVehicle`, `renewalVehicle`,
 * `blankVehicle`) and a factory for building a `JourneyContext` suitable for
 * renewal journey component tests.
 */

import { getTestProductVariant } from './productHelper';

export const testVehicle: VehicleType = {
  vrn: 'YA66FTK',
  vin: '000111',
  make: 'Ford',
  model: 'Kuga',
  engineSize: '2000',
  errorMessage: '',
  fuelType: 'Diesel',
  modelGroup: 'Zetec',
  modelTrimId: 3,
  registrationDate: '{"value":"2015-12-24T12:00:00","utc":"0"}',
  vehicleType: 'SUV',
  fuelTypeId: 2,
  fuelTypeCode: '00100002',
  enginePowerBHP: 0,
  enginePowerKWH: 0,
  driveTypeId: 0,
  modelType: 'Zetec',
  makeId: '',
  modelId: '0',
  vehicleTypeId: '0'
};

export const renewalVehicle: VehicleType = {
  vrn: 'yh19drl',
  vin: '1233',
  make: 'Toyota',
  model: 'Innova',
  engineSize: '2.5L',
  errorMessage: 'No',
  fuelType: 'Diesel',
  modelGroup: 'Jeep',
  modelTrimId: 0,
  registrationDate: '{"value":"2022-03-18T12:00:00","utc":"0"}',
  vehicleType: 'SUV',
  fuelTypeId: 2,
  fuelTypeCode: '00100002',
  enginePowerBHP: 300,
  enginePowerKWH: 2000,
  driveTypeId: 2,
  modelType: '',
  makeId: '',
  modelId: '0',
  vehicleTypeId: '0'
};

export const defaultVehiclePageCustomisation: VehiclePageCustomisation = {
  title: '',
  description: '',
  information: '',
  warning: '',
  justSoYouKnow: '',
  upsellAnnualMileage: '',
  hideDealerReference: false,
  hideDealerReferenceCCI: false,
  hidePurchasePrice: false,
  hideDeliveryDate: false,
  hideMileage: false,
  editablePurchasePrice: false,
  editableMileage: false,
  hideEstimatedDeliveryDate: false,
  mandatorySalesId: false,
  externalReferenceLabelText: '',
  hideVinInput: false,
  mandatoryExternalReference: false,
  mandatoryExternalReferenceCCI: false,
  mandatoryVrn: false,
  mandatoryVin: false
};

export const blankVehicle: VehicleType = {
  vrn: '',
  vin: '',
  make: '',
  model: '',
  engineSize: '',
  errorMessage: '',
  fuelType: '',
  modelGroup: '',
  modelTrimId: 0,
  registrationDate: '',
  vehicleType: '',
  fuelTypeId: 0,
  fuelTypeCode: '',
  enginePowerBHP: 0,
  enginePowerKWH: 0,
  driveTypeId: 0,
  modelType: '',
  makeId: '',
  modelId: '0',
  vehicleTypeId: '0'
};

export const getRenewalsJourneyContext = (
  journey: Partial<JourneyState> = {}
): JourneyContext => {
  return {
    journey: {
      personalDetail: {
        title: 'Mr',
        firstName: 'John',
        surname: 'Mark',
        dateOfBirth: undefined,
        email: 'j@m.com',
        confirmEmail: 'j@l.com',
        phone: '888888888',
        addressDetails: {
          addressLine1: '',
          addressLine2: '',
          townOrCity: '',
          region: '',
          country: '',
          postCode: ''
        }
      },
      vehicle: {
        vehicleDetails: {
          vrn: 'yh19drl',
          vin: '1233',
          make: 'Toyota',
          model: 'Innova',
          engineSize: '2.5L',
          errorMessage: 'No',
          fuelType: 'Diesel',
          modelGroup: 'Jeep',
          modelTrimId: 0,
          registrationDate: '{"value":"2022-03-18T12:00:00","utc":"0"}',
          vehicleType: 'SUV',
          fuelTypeId: 2,
          fuelTypeCode: '00100002',
          enginePowerBHP: 300,
          enginePowerKWH: 2000,
          driveTypeId: 2,
          modelType: '',
          makeId: '',
          modelId: '0',
          vehicleTypeId: '0'
        },
        currentMileage: null,
        vehiclePurchasePrice: 0,
        vehicleDeliveryDate: undefined,
        salesId: '',
        dealerReference: '',
        deliveryDateEstimated: false
      },
      products: journey.products ?? [
        {
          productName: 'GAP Insurance',
          productTypeId: 26,
          parentProductTypeId: 26,
          productTypeCode: '',
          productSubTypeId: 0,
          productId: 1,
          policyStartDate: '',
          policyStartDateLimit: 30,
          isVariablePrice: false,
          isSelected: true,
          isPurchased: false,
          productVariants: [
            getTestProductVariant({
              variantId: 'var1',
              warrantyTierId: 0,
              coverLevelId: 0,
              excess: 0,
              customerFriendlyName: ''
            })
          ],
          renewalGroupId: '1',
          productDocuments: [
            {
              documentTypeId: 0,
              code: '0',
              displayName: 'test',
              warrantyTierId: 0,
              productTypeId: 26,
              productId: 1
            }
          ],
          effectiveDate: '',
          expiryDate: ''
        },
        {
          productName: 'Cosmetic Insurance',
          productTypeId: 25,
          parentProductTypeId: 25,
          productTypeCode: '',
          productSubTypeId: 0,
          productId: 2,
          policyStartDate: '',
          policyStartDateLimit: 30,
          isVariablePrice: false,
          isSelected: true,
          isPurchased: false,
          productVariants: [
            getTestProductVariant({
              variantId: 'var2',
              productId: 2,
              price: 50,
              warrantyTierId: 0,
              coverLevelId: 0,
              excess: 0,
              customerFriendlyName: ''
            }),
            getTestProductVariant({
              variantId: 'var3',
              productId: 2,
              duration: '24',
              price: 100,
              warrantyTierId: 0,
              coverLevelId: 0,
              excess: 0,
              customerFriendlyName: ''
            })
          ],
          renewalGroupId: '2',
          productDocuments: [
            {
              documentTypeId: 0,
              code: '0',
              displayName: 'test',
              warrantyTierId: 0,
              productTypeId: 25,
              productId: 2
            }
          ],
          effectiveDate: '',
          expiryDate: ''
        }
      ],
      directDebitDetails: {
        accountHolderName: '',
        sortCode1: null,
        sortCode2: null,
        sortCode3: null,
        accountNumber: null,
        selectedPaymentDay: '',
        bankName: ''
      },
      navigationStatus: {
        vehicle: false,
        products: false,
        personalDetails: false,
        checkout: false,
        confirmation: false,
        quote: false
      },
      isLateRenewal: false,
      promoCode: '',
      dealerCode: '',
      agreedTermsAndConditions: false,
      renewalMarketing: false,
      documents: [],
      expandedDocIds: [],
      orderNumbers: [],
      policyStartDate: new Date(),
      token: '',
      tokenExpiresOn: new Date(),
      singlePaymentDetails: {
        billingAddress: {}
      },
      registrationState: 0,
      registrationFailMsg: '',
      bankAuthorisationCode: '',
      foundExistingQuotes: false,
      quoteId: 0,
      hasQuoteExpired: false,
      quoteProducts: [],
      isExpiredMessageAcknowledged: false
    },
    onUpdateVehicleDetails: jest.fn(),
    onChangeSalesId: jest.fn(),
    onUpdateProducts: jest.fn(),
    onUpdateQuotedProducts: jest.fn(),
    onUpdateProductDocuments: jest.fn(),
    onUpdateProductVariant: jest.fn(),
    setPersonalDetails: jest.fn(),
    setAddressSinglePayment: jest.fn(),
    setDirectDebitDetails: jest.fn(),
    onUpdateAgreedTermsAndConditions: jest.fn(),
    onUpdateRenewalMarketing: jest.fn(),
    onRegister: jest.fn(),
    onPaymentFailure: jest.fn(),
    onPaymentVoided: jest.fn(),
    onPageChange: jest.fn(),
    getPolicy: jest.fn(),
    setRegistrationFailMsg: jest.fn(),
    setExpandedDocIds: jest.fn(),
    onUpdateFoundExistingQuotes: jest.fn(),
    onPolicyStartDateChange: jest.fn(),
    onChangeMileage: jest.fn(),
    onChangeVehiclePurchasePrice: jest.fn(),
    onChangeVehicleDeliveryDate: jest.fn(),
    onChangeVehicleGapIntroductionDate: jest.fn(),
    onUpdateVehicleVrn: jest.fn(),
    onSetJourney: jest.fn(),
    onProductDocumentsDownloaded: jest.fn(),
    onUpdateManufacturingCoverageMonths: jest.fn()
  };
};
